import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, DollarSign, Clock, Trophy, XCircle, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import AdminLayout from '../../components/AdminLayout';
import WinningTheWeek from '../../components/admin/WinningTheWeek';
import api from '../../utils/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    total: 0, submitted: 0, quoted: 0, closedWon: 0, closedLost: 0, winRate: 0, totalPremium: 0
  });
  const [recentApplications, setRecentApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboardData(); }, []);

  const loadDashboardData = async () => {
    try {
      const response = await api.get('/applications');
      const applications = response.data;
      const closedWon = applications.filter(a => a.status === 'closed_won' || a.status === 'accepted' || a.status === 'bound').length;
      const closedLost = applications.filter(a => a.status === 'closed_lost').length;
      const totalClosed = closedWon + closedLost;
      const totalPremium = applications
        .filter(a => (a.status === 'closed_won' || a.status === 'accepted' || a.status === 'bound') && a.bound_premium)
        .reduce((sum, a) => sum + (a.bound_premium || 0), 0);
      setStats({
        total: applications.length,
        submitted: applications.filter(a => a.status === 'submitted').length,
        quoted: applications.filter(a => a.status === 'quoted').length,
        closedWon, closedLost,
        winRate: totalClosed > 0 ? Math.round((closedWon / totalClosed) * 100) : 0,
        totalPremium
      });
      setRecentApplications(applications.slice(0, 5));
    } catch (error) {
      console.error('Failed to load dashboard data', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      submitted:   { bg: 'bg-[#FFFBEB]', border: 'border-[#FDE68A]', text: 'text-[#92400E]', label: 'Submitted' },
      quoted:      { bg: 'bg-[#EEF0FF]', border: 'border-[#C4BFFF]', text: 'text-[#6B5CE7]', label: 'Quoted' },
      accepted:    { bg: 'bg-[#ECFDF5]', border: 'border-[#A7F3D0]', text: 'text-[#065F46]', label: 'Closed Won' },
      bound:       { bg: 'bg-[#ECFDF5]', border: 'border-[#A7F3D0]', text: 'text-[#065F46]', label: 'Closed Won' },
      closed_won:  { bg: 'bg-[#ECFDF5]', border: 'border-[#A7F3D0]', text: 'text-[#065F46]', label: 'Closed Won' },
      closed_lost: { bg: 'bg-[#FEF2F2]', border: 'border-[#FECACA]', text: 'text-[#991B1B]', label: 'Closed Lost' },
      declined:    { bg: 'bg-[#F3F4F6]', border: 'border-[#E5E7EB]', text: 'text-[#374151]', label: 'Declined' }
    };
    const config = statusConfig[status] || statusConfig.submitted;
    return (
      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${config.bg} ${config.border} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getApplicantName = (app) => {
    if (app.first_name && app.last_name) return `${app.first_name} ${app.last_name}`;
    return app.applicant_name || 'N/A';
  };

  const isWonStatus = (status) => status === 'closed_won' || status === 'accepted' || status === 'bound';

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7B6FE8]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-black text-[#1A1A1A] uppercase tracking-wide" style={{fontFamily: "'Barlow Condensed', sans-serif"}}>
            Dashboard
          </h1>
          <p className="text-[#6B7280] mt-1 text-sm" style={{fontFamily: "'Inter', sans-serif", textTransform: 'none', fontWeight: 400, letterSpacing: 'normal'}}>
            Overview of your insurance applications
          </p>
        </div>

        {/* Winning The Week — biz-dev scoreboard */}
        <WinningTheWeek />

        {/* Pipeline Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card data-testid="stat-total-applications" className="bg-white border border-[#E5E7EB] shadow-none hover:shadow-md hover:shadow-[#6B5CE7]/5 transition-shadow relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#6B7280]" style={{fontFamily: "'Inter', sans-serif", textTransform: 'none', fontWeight: 500, letterSpacing: 'normal'}}>Total Applications</CardTitle>
              <div className="p-2 bg-[#EEF0FF] rounded-lg">
                <FileText className="h-5 w-5 text-[#7B6FE8]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-[#1A1A1A]" style={{fontFamily: "'Barlow Condensed', sans-serif"}}>{stats.total}</div>
            </CardContent>
          </Card>

          <Card data-testid="stat-submitted" className="bg-white border border-[#E5E7EB] shadow-none hover:shadow-md hover:shadow-[#6B5CE7]/5 transition-shadow relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#6B7280]" style={{fontFamily: "'Inter', sans-serif", textTransform: 'none', fontWeight: 500, letterSpacing: 'normal'}}>Awaiting Quote</CardTitle>
              <div className="p-2 bg-[#FFFBEB] rounded-lg">
                <Clock className="h-5 w-5 text-[#92400E]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-[#1A1A1A]" style={{fontFamily: "'Barlow Condensed', sans-serif"}}>{stats.submitted}</div>
            </CardContent>
          </Card>

          <Card data-testid="stat-quoted" className="bg-white border border-[#E5E7EB] shadow-none hover:shadow-md hover:shadow-[#6B5CE7]/5 transition-shadow relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#6B7280]" style={{fontFamily: "'Inter', sans-serif", textTransform: 'none', fontWeight: 500, letterSpacing: 'normal'}}>Quotes Sent</CardTitle>
              <div className="p-2 bg-[#EEF0FF] rounded-lg">
                <DollarSign className="h-5 w-5 text-[#7B6FE8]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-[#1A1A1A]" style={{fontFamily: "'Barlow Condensed', sans-serif"}}>{stats.quoted}</div>
            </CardContent>
          </Card>
        </div>

        {/* Won/Lost Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card data-testid="stat-closed-won" className="bg-white border border-[#E5E7EB] shadow-none hover:shadow-md hover:shadow-[#6B5CE7]/5 transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#6B7280]" style={{fontFamily: "'Inter', sans-serif", textTransform: 'none', fontWeight: 500, letterSpacing: 'normal'}}>Closed Won</CardTitle>
              <div className="p-2 bg-[#ECFDF5] rounded-lg">
                <Trophy className="h-5 w-5 text-[#065F46]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-[#065F46]" style={{fontFamily: "'Barlow Condensed', sans-serif"}}>{stats.closedWon}</div>
              {stats.totalPremium > 0 && (
                <p className="text-sm text-[#6B7280] mt-1">${stats.totalPremium.toLocaleString()} total premium</p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="stat-closed-lost" className="bg-white border border-[#E5E7EB] shadow-none hover:shadow-md hover:shadow-[#6B5CE7]/5 transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#6B7280]" style={{fontFamily: "'Inter', sans-serif", textTransform: 'none', fontWeight: 500, letterSpacing: 'normal'}}>Closed Lost</CardTitle>
              <div className="p-2 bg-[#FEF2F2] rounded-lg">
                <XCircle className="h-5 w-5 text-[#991B1B]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-[#991B1B]" style={{fontFamily: "'Barlow Condensed', sans-serif"}}>{stats.closedLost}</div>
            </CardContent>
          </Card>

          <Card data-testid="stat-win-rate" className="bg-white border border-[#E5E7EB] shadow-none hover:shadow-md hover:shadow-[#6B5CE7]/5 transition-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#6B7280]" style={{fontFamily: "'Inter', sans-serif", textTransform: 'none', fontWeight: 500, letterSpacing: 'normal'}}>Win Rate</CardTitle>
              <div className="p-2 bg-[#EEF0FF] rounded-lg">
                <TrendingUp className="h-5 w-5 text-[#7B6FE8]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black text-[#7B6FE8]" style={{fontFamily: "'Barlow Condensed', sans-serif"}}>{stats.winRate}%</div>
              <p className="text-sm text-[#6B7280] mt-1">{stats.closedWon + stats.closedLost} total closed</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Applications */}
        <Card className="bg-white border border-[#E5E7EB] shadow-none relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
          <CardHeader>
            <CardTitle className="text-lg font-black text-[#1A1A1A] uppercase tracking-wide" style={{fontFamily: "'Barlow Condensed', sans-serif"}}>
              Recent Applications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentApplications.length === 0 ? (
              <p className="text-[#6B7280] text-center py-8">No applications yet</p>
            ) : (
              <div className="space-y-3">
                {recentApplications.map((app) => (
                  <Link
                    key={app.id}
                    to={`/admin/applications/${app.id}`}
                    data-testid={`application-${app.id}`}
                    className="block border border-[#E5E7EB] rounded-lg p-4 hover:bg-[#EEF0FF]/30 hover:border-[#7B6FE8]/20 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-[#1A1A1A]">{app.org_name}</h4>
                        <p className="text-sm text-[#6B7280] mt-1">
                          {getApplicantName(app)} &middot; {app.affiliate_name}
                        </p>
                        {isWonStatus(app.status) && app.bound_premium && (
                          <Badge variant="outline" className="mt-2 bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]">
                            <DollarSign className="h-3 w-3 mr-1" />
                            ${app.bound_premium.toLocaleString()} premium
                          </Badge>
                        )}
                      </div>
                      <div className="text-right">
                        {getStatusBadge(app.status)}
                        <p className="text-sm text-[#9CA3AF] mt-1">
                          {new Date(app.submitted_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
